import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTrackEvent, ANALYTICS_EVENTS } from '@resida/analytics';
import { PLAN_COPY, TRIAL_LISTING_POINT, type PlanCopyPoint } from '@resida/api-types/plan-copy';
import type { PublicPricingResponse } from '@resida/api-types/billing';
import { usePublicPricing, retryPublicPricing, formatPriceCents } from '../../hooks/usePublicPricing';
import {
  PRICING_CARD_COPY,
  PRICING_SECTION_COPY,
  listingUnitText,
  trialPillText,
} from '../../data/pricing-content';

const isPreLaunch = import.meta.env.VITE_PRE_LAUNCH === 'true';

type Plan = PublicPricingResponse['plans'][number];

function CheckList({ items }: { items: readonly PlanCopyPoint[] }) {
  return (
    <ul className="space-y-3 text-left">
      {items.map((item) => (
        <li key={item.name} className="flex items-start gap-3 text-sm text-[var(--landing-text-muted)]">
          <span className="text-[var(--landing-accent-text)] font-bold flex-shrink-0 mt-0.5" aria-hidden="true">
            &#10003;
          </span>
          <span>
            {item.featureSlug ? (
              <Link to={`/features/${item.featureSlug}`} className="hover:text-[var(--landing-accent-text)] transition-colors">
                {item.name}
              </Link>
            ) : (
              item.name
            )}
            {item.description && (
              <span className="block mt-0.5 text-[var(--landing-text-muted)]">{item.description}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PriceBlock({ price, unit }: { price: string; unit: string }) {
  return (
    <div className="mb-6">
      <div className="text-4xl font-bold text-[var(--landing-text-primary)] mb-1">{price}</div>
      <p className="text-sm text-[var(--landing-text-muted)]">{unit}</p>
    </div>
  );
}

/** An optional add-on block inside a plan card (listings, agent support): title, price line, shared bullets. */
function OfferBlock({
  title,
  price,
  unit,
  points,
  testId,
}: {
  title: string;
  price: string;
  unit: string;
  points: readonly PlanCopyPoint[];
  testId: string;
}) {
  return (
    <div data-testid={testId} className="border-t border-[var(--landing-border)] pt-5 mt-5">
      <h4 className="text-base font-semibold text-[var(--landing-text-primary)] mb-2">{title}</h4>
      <p className="mb-4">
        <span className="text-2xl font-bold text-[var(--landing-text-primary)]">{price}</span>{' '}
        <span className="text-sm text-[var(--landing-text-muted)]">{unit}</span>
      </p>
      <CheckList items={points} />
    </div>
  );
}

/** Cards sit in a centred, wrapping row: one to any number of plans fit (the count is data-driven, BR-107). */
function PlanGrid({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-start justify-center gap-6">{children}</div>;
}

/** One plan-sized card; the skeleton and the real cards share it so the layout does not shift. */
function CardShell({ testId, pulse, children }: { testId?: string; pulse?: boolean; children: ReactNode }) {
  return (
    <div
      data-testid={testId}
      className={`w-full max-w-md md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-border)] flex flex-col ${pulse ? 'animate-pulse' : ''}`}
    >
      {children}
    </div>
  );
}

function PlanCard({ plan, trialListings }: { plan: Plan; trialListings: boolean }) {
  return (
    <CardShell testId={`pricing-card-${plan.id}`}>
      <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-4">{plan.name}</h3>
      <PriceBlock price={formatPriceCents(plan.unitPriceCents)} unit={PRICING_CARD_COPY.perUnitMonth} />

      {plan.listingFeeCents !== null && (
        <OfferBlock
          testId={`pricing-card-listings-${plan.id}`}
          title={PLAN_COPY.listing.title}
          price={formatPriceCents(plan.listingFeeCents)}
          unit={listingUnitText(plan.listingPeriodDays)}
          points={trialListings ? [...PLAN_COPY.listing.points, TRIAL_LISTING_POINT] : PLAN_COPY.listing.points}
        />
      )}

      {plan.agentUnitPriceCents !== null && (
        <OfferBlock
          testId={`pricing-card-agent-${plan.id}`}
          title={PLAN_COPY.agent.title}
          price={formatPriceCents(plan.agentUnitPriceCents)}
          unit={PRICING_CARD_COPY.perUnitMonth}
          points={PLAN_COPY.agent.points}
        />
      )}
    </CardShell>
  );
}

function PricingSkeleton() {
  return (
    <div data-testid="pricing-skeleton" aria-busy="true" aria-label={PRICING_CARD_COPY.loadingLabel}>
      <PlanGrid>
        {[0, 1, 2].map((i) => (
          <CardShell key={i} pulse>
            <div className="h-5 w-32 rounded bg-[var(--landing-border)] mb-6" />
            <div className="h-10 w-28 rounded bg-[var(--landing-border)] mb-3" />
            <div className="h-4 w-40 rounded bg-[var(--landing-border)]" />
          </CardShell>
        ))}
      </PlanGrid>
    </div>
  );
}

/** What every plan includes, once: the shared PLAN_COPY, so it is in the prerendered HTML. */
function IncludedList() {
  return (
    <section
      data-testid="pricing-included"
      aria-labelledby="pricing-included-heading"
      className="max-w-3xl w-full mx-auto bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-border)]"
    >
      <h3 id="pricing-included-heading" className="text-lg font-semibold text-[var(--landing-text-primary)] mb-4">
        {PRICING_CARD_COPY.includedHeading}
      </h3>
      <CheckList items={PLAN_COPY.included} />
    </section>
  );
}

interface PricingCardsProps {
  /** data-testid of the primary call to action (kept per page for analytics/e2e). */
  ctaTestId: string;
  /** Analytics location of the call to action. */
  ctaLocation: string;
}

/**
 * One public price card per plan, fed by GET /api/public/pricing (ascending unit price, as served),
 * then the shared "Every plan includes" list once (PLAN_COPY). Skeleton until the fetch resolves;
 * "Pricing coming soon" when no plan is advertised; a retryable error when the request failed.
 */
export function PricingCards({ ctaTestId, ctaLocation }: PricingCardsProps): JSX.Element {
  const trackEvent = useTrackEvent();
  const state = usePublicPricing();

  if (state.status === 'loading') {
    return (
      <div className="flex flex-col gap-10">
        <PricingSkeleton />
        <IncludedList />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex flex-col gap-10">
        <div
          role="alert"
          className="max-w-lg mx-auto bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-border)] text-center"
          data-testid="pricing-error"
        >
          <div className="text-2xl font-bold text-[var(--landing-text-primary)] mb-2">{PRICING_CARD_COPY.errorTitle}</div>
          <p className="text-sm text-[var(--landing-text-muted)] mb-4">{PRICING_CARD_COPY.errorBody}</p>
          <button
            type="button"
            data-testid="pricing-retry"
            className="bg-[var(--landing-accent)] hover:bg-[var(--landing-accent-hover)] text-white font-semibold rounded-[var(--landing-radius)] px-6 min-h-[44px] transition-colors"
            onClick={retryPublicPricing}
          >
            {PRICING_CARD_COPY.retryLabel}
          </button>
        </div>
        <IncludedList />
      </div>
    );
  }

  const { pricing } = state;

  if (pricing.plans.length === 0) {
    return (
      <div className="flex flex-col gap-10">
        <div
          className="max-w-lg mx-auto bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-8 border border-[var(--landing-accent)] text-center"
          data-testid="pricing-coming-soon"
        >
          <div className="text-3xl font-bold text-[var(--landing-text-primary)] mb-2">{PRICING_CARD_COPY.comingSoonTitle}</div>
          <p className="text-sm text-[var(--landing-text-muted)]">{trialPillText(pricing.trial?.durationDays)}</p>
        </div>
        <IncludedList />
      </div>
    );
  }

  const trialListings = (pricing.trial?.listingFeeCents ?? null) !== null;

  return (
    <div className="flex flex-col gap-10" data-testid="pricing-cards">
      <div className="text-center">
        <span
          className="bg-[var(--landing-accent)] text-white rounded-full px-4 py-1 text-sm font-medium"
          data-testid="pricing-trial-pill"
        >
          {trialPillText(pricing.trial?.durationDays)}
        </span>
      </div>

      <PlanGrid>
        {pricing.plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} trialListings={trialListings} />
        ))}
      </PlanGrid>

      <IncludedList />

      <div hidden={isPreLaunch} className="max-w-sm w-full mx-auto">
        <a
          href="/landlord/login"
          data-testid={ctaTestId}
          className="block w-full text-center bg-[var(--landing-accent)] hover:bg-[var(--landing-accent-hover)] text-white font-semibold rounded-[var(--landing-radius)] px-6 min-h-[44px] flex items-center justify-center transition-colors"
          onClick={() => trackEvent(ANALYTICS_EVENTS.LANDING_CTA_CLICKED, { location: ctaLocation })}
        >
          {PRICING_CARD_COPY.ctaLabel}
        </a>
      </div>
    </div>
  );
}

export function Pricing(): JSX.Element {
  return (
    <section
      id="pricing"
      data-testid="landing-pricing"
      className="py-[var(--landing-section-py)] bg-[var(--landing-bg)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--landing-text-primary)] mb-4">
            {PRICING_SECTION_COPY.heading}
          </h2>
          <p className="text-lg text-[var(--landing-text-muted)]">
            {PRICING_SECTION_COPY.intro}
          </p>
        </div>
        <PricingCards ctaTestId="pricing-cta" ctaLocation="pricing" />
      </div>
    </section>
  );
}
